import React, { Suspense, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'reactstrap';
import { MdBusinessCenter, MdInventory, MdTrendingDown, MdTrendingUp, MdLocationOn } from 'react-icons/md';
import { toast } from 'react-toastify';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { fetchManufacturerDistributorStats } from '../../../../services/Http/ManufacturerApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const DistributorStats = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const response = await fetchManufacturerDistributorStats();
                setStats(response);
            } catch (err) {
                setError(true);
                toast.error(t('ManufacturerDistributors.errors.loadStats'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [t]);

    const totals = stats?.totals || {};
    const trendData = stats?.trend || [];
    const countryStats = stats?.countries || [];

    const statCards = [
        {
            key: 'distributors',
            label: t('ManufacturerDistributors.stats.cards.distributors'),
            value: (totals.distributors || 0).toLocaleString(),
            icon: <MdBusinessCenter className="icon" />,
            color: 'totalVolume',
        },
        {
            key: 'inventory',
            label: t('ManufacturerDistributors.stats.cards.inventory'),
            value: (totals.inventory || 0).toLocaleString(),
            icon: <MdInventory className="icon" />,
            color: 'venues',
        },
        {
            key: 'sold',
            label: t('ManufacturerDistributors.stats.cards.sold'),
            value: (totals.sold || 0).toLocaleString(),
            icon: <MdTrendingUp className="icon" />,
            color: 'soldItems',
        },
        {
            key: 'inbound',
            label: t('ManufacturerDistributors.stats.cards.inbound'),
            value: formatResolvedAmount({
                eurAmount: totals.inbound_value,
                localAmount: totals.inbound_value_local ?? totals.inbound_local,
                localCurrencyId: totals.inbound_currency_id ?? totals.currency_id,
                localCurrencyCode: totals.inbound_currency_code ?? totals.currency_code,
            }),
            icon: <MdTrendingDown className="icon" />,
            color: 'transactions',
        },
        {
            key: 'outbound',
            label: t('ManufacturerDistributors.stats.cards.outbound'),
            value: formatResolvedAmount({
                eurAmount: totals.outbound_value,
                localAmount: totals.outbound_value_local ?? totals.outbound_local,
                localCurrencyId: totals.outbound_currency_id ?? totals.currency_id,
                localCurrencyCode: totals.outbound_currency_code ?? totals.currency_code,
            }),
            icon: <MdTrendingUp className="icon" />,
            color: 'revenue',
        },
        {
            key: 'venues',
            label: t('ManufacturerDistributors.stats.cards.venues'),
            value: (totals.venues || 0).toLocaleString(),
            icon: <MdLocationOn className="icon" />,
            color: 'users',
        },
    ];

    const chartSeries = useMemo(() => [
        {
            name: t('ManufacturerDistributors.stats.trend.inbound'),
            data: trendData.map((point) => point.inbound_value || 0),
        },
        {
            name: t('ManufacturerDistributors.stats.trend.outbound'),
            data: trendData.map((point) => point.outbound_value || 0),
        },
    ], [trendData, t]);

    const chartCategories = useMemo(
        () => trendData.map((point) => point.date),
        [trendData]
    );

    if (loading) {
        return <LoadingMessage>{t('ManufacturerDistributors.stats.loading')}</LoadingMessage>;
    }

    if (error || !stats) {
        return <ErrorMessage>{t('ManufacturerDistributors.stats.error')}</ErrorMessage>;
    }

    return (
        <div>
            <StatsGrid>
                {statCards.map((card) => (
                    <StatCard
                        key={card.key}
                        icon={card.icon}
                        title={card.label}
                        value={card.value}
                        color={card.color}
                    />
                ))}
            </StatsGrid>

            <Grid>
                <Card>
                    <SectionTitle>{t('ManufacturerDistributors.stats.trend.title')}</SectionTitle>
                    {trendData.length === 0 ? (
                        <MutedText>{t('ManufacturerDistributors.stats.trend.empty')}</MutedText>
                    ) : (
                        <Suspense fallback={<Centered><Spinner /></Centered>}>
                            <ReactApexChart
                                type="area"
                                height={320}
                                series={chartSeries}
                                options={{
                                    chart: { id: 'manufacturer-distributors-trend', toolbar: { show: false } },
                                    xaxis: { categories: chartCategories },
                                    stroke: { width: 3 },
                                    dataLabels: { enabled: false },
                                    colors: ['#821692', '#0DCAF0'],
                                    fill: {
                                        type: 'gradient',
                                        gradient: {
                                            shadeIntensity: 1,
                                            opacityFrom: 0.5,
                                            opacityTo: 0.1,
                                            stops: [0, 90, 100],
                                        },
                                    },
                                }}
                            />
                        </Suspense>
                    )}
                </Card>

                <Card>
                    <SectionTitle>{t('ManufacturerDistributors.stats.countries.title')}</SectionTitle>
                    {countryStats.length === 0 ? (
                        <MutedText>{t('ManufacturerDistributors.stats.countries.empty')}</MutedText>
                    ) : (
                        <CountryList>
                            {countryStats.map((country) => (
                                <li key={country.country}>
                                    <span>{country.country}</span>
                                    <strong>{country.total}</strong>
                                </li>
                            ))}
                        </CountryList>
                    )}
                </Card>
            </Grid>
        </div>
    );
};

export default DistributorStats;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.25rem;
    margin-top: 1.75rem;
`;

const Card = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1.25rem;
`;

const SectionTitle = styled.h3`
    margin: 0 0 1rem 0;
    color: ${({ theme }) => theme.colors.textPrimary || '#333'};
`;

const MutedText = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const CountryList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    li {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px dashed rgba(130, 22, 146, 0.2);
        padding-bottom: 0.5rem;

        span {
            font-weight: 500;
        }

        strong {
            color: rgba(130, 22, 146, 1);
        }
    }
`;

const Centered = styled.div`
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
`;
